import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { CircularProgress } from "@material-ui/core";
import {
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
  Typography,
  Paper,
  Box,
  Button
} from "@mui/material";
import {
  AccessTime,
  CallSplit,
  DynamicFeed,
  LibraryBooks,
  RocketLaunch,
  Save as SaveIcon,
  ConfirmationNumber,
  ArrowBack,
  AutoFixHigh as AutoFixIcon,
  GetApp,
  Tag,
  Person,
  Upload,
  KeyboardReturn
} from "@mui/icons-material";
import { HiOutlinePuzzle } from "react-icons/hi";
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge
} from "react-flow-renderer";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useNodeStorage } from "../../stores/useNodeStorage";

import messageNode from "./nodes/messageNode";
import startNode from "./nodes/startNode";
import conditionNode from "./nodes/conditionNode";
import menuNode from "./nodes/menuNode";
import intervalNode from "./nodes/intervalNode";
import imgNode from "./nodes/imgNode";
import randomizerNode from "./nodes/randomizerNode";
import videoNode from "./nodes/videoNode";
import singleBlockNode from "./nodes/singleBlockNode";
import ticketNode from "./nodes/ticketNode";
import tagNode from "./nodes/tagNode";
import attendantNode from "./nodes/attendantNode";
import questionNode from "./nodes/questionNode";
import audioNode from "./nodes/audioNode";
import RemoveEdge from "./nodes/removeEdge";
import buttonsNode from "./nodes/buttonsNode";
import fallbackNode from "./nodes/fallbackNode";
import inactivityNode from "./nodes/inactivityNode";

import FlowBuilderAddTextModal from "../../components/FlowBuilderAddTextModal";
import FlowBuilderIntervalModal from "../../components/FlowBuilderIntervalModal";
import FlowBuilderConditionModal from "../../components/FlowBuilderConditionModal";
import FlowBuilderMenuModal from "../../components/FlowBuilderMenuModal";
import FlowBuilderAddImgModal from "../../components/FlowBuilderAddImgModal";
import FlowBuilderTicketModal from "../../components/FlowBuilderAddTicketModal";
import FlowBuilderAddQuestionModal from "../../components/FlowBuilderAddQuestionModal";
import FlowBuilderAddAudioModal from "../../components/FlowBuilderAddAudioModal";
import FlowBuilderAddTagModal from "../../components/FlowBuilderAddTagModal";
import FlowBuilderAddAttendantModal from "../../components/FlowBuilderAddAttendantModal"; // Import do modal de atendente
import FlowBuilderRandomizerModal from "../../components/FlowBuilderRandomizerModal";
import FlowBuilderAddVideoModal from "../../components/FlowBuilderAddVideoModal";
import FlowBuilderSingleBlockModal from "../../components/FlowBuilderSingleBlockModal";
import FlowBuilderButtonsModal from "../../components/FlowBuilderButtonsModal";
import FlowBuilderFallbackModal from "../../components/FlowBuilderFallbackModal";
import FlowBuilderInactivityModal from "../../components/FlowBuilderInactivityModal";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    flex: 1,
    height: "100vh",
    backgroundColor: theme.palette.background.default
  },
  flowContainer: {
    flex: 1,
    height: "calc(100vh - 64px)",
    position: "relative"
  },
  header: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  actionButtons: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    "& .MuiButton-root": {
      textTransform: "none",
      fontWeight: 500
    }
  },
  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  },
  organizeButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.success.dark
    }
  },
  backButton: {
    marginRight: theme.spacing(2),
    color: theme.palette.text.primary
  },
  speedDial: {
    position: "absolute",
    top: theme.spacing(2),
    left: theme.spacing(2),
    "& .MuiSpeedDial-fab": {
      backgroundColor: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark
      }
    }
  }
}));

const nodeTypes = {
  message: messageNode,
  start: startNode,
  condition: conditionNode,
  menu: menuNode,
  interval: intervalNode,
  img: imgNode,
  audio: audioNode,
  randomizer: randomizerNode,
  video: videoNode,
  singleBlock: singleBlockNode,
  ticket: ticketNode,
  tag: tagNode,
  attendant: attendantNode,
  question: questionNode
  ,
  buttons: buttonsNode,
  inactivity: inactivityNode
};

const edgeTypes = {
  buttonedge: RemoveEdge
};

const initialNodes = [
  {
    id: "1",
    position: { x: 250, y: 100 },
    data: { label: "Inicio do fluxo" },
    type: "start"
  }
];

function geraStringAleatoria(tamanho) {
  let stringAleatoria = "";
  const caracteres = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < tamanho; i++) {
    stringAleatoria += caracteres.charAt(Math.floor(Math.random() * caracteres.length));
  }
  return stringAleatoria;
}

export const FlowBuilderConfig = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { id } = useParams();
  const storageItems = useNodeStorage();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [dataNode, setDataNode] = useState(null);
  const [flowName, setFlowName] = useState("");

  const [modalStates, setModalStates] = useState({
    text: null,
    interval: false,
    condition: null,
    menu: null,
    buttons: null,
    img: null,
    audio: null,
    randomizer: null,
    video: null,
    singleBlock: null,
    ticket: null,
    modalAddTag: null,
    modalAddAttendant: null,
    question: null,
    inactivity: null
  });

  const connectionLineStyle = {
    stroke: theme.palette.primary.main,
    strokeWidth: "5px"
  };

  // Ações que aparecem no SpeedDial (menu lateral)
  const actions = [
    {
      icon: <RocketLaunch sx={{ color: theme.palette.success.main }} />,
      name: "Inicio",
      type: "start"
    },
    {
      icon: <LibraryBooks sx={{ color: theme.palette.error.main }} />,
      name: "Conteúdo",
      type: "content"
    },
    {
      icon: <DynamicFeed sx={{ color: theme.palette.secondary.main }} />,
      name: "Menu",
      type: "menu"
    },
    {
      icon: <AutoFixIcon sx={{ color: theme.palette.secondary.main }} />,
      name: "Botões",
      type: "buttons"
    },
    {
      icon: <CallSplit sx={{ color: theme.palette.info.main }} />,
      name: "Randomizador",
      type: "random"
    },
    {
      icon: <AccessTime sx={{ color: theme.palette.warning.main }} />,
      name: "Intervalo",
      type: "interval"
    },
    {
      icon: <ConfirmationNumber sx={{ color: theme.palette.info.main }} />,
      name: "Departamento",
      type: "ticket"
    },
    {
      icon: <HiOutlinePuzzle style={{ color: theme.palette.secondary.main }} />,
      name: "Pergunta",
      type: "question"
    },
    {
      icon: <Tag style={{ color: theme.palette.secondary.main }} />,
      name: "Tags",
      type: "tag"
    },
    {
      icon: <Person style={{ color: theme.palette.secondary.main }} />,
      name: "Atendente",
      type: "attendant"
    },
    {
      icon: <AccessTime style={{ color: theme.palette.primary.main }} />,
      name: "Inatividade",
      type: "inactivity"
    }
  ];

  // Função geral para adicionar nós
  const addNode = (type, data) => {
    const posY = nodes[nodes.length - 1].position.y;
    const posX =
      nodes[nodes.length - 1].position.x +
      (nodes[nodes.length - 1].width || 0) +
      40;

    const newNodes = {
      start: {
        id: "1",
        position: { x: posX, y: posY },
        data: { label: "Inicio do fluxo" },
        type: "start"
      },
      text: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { label: data.text },
        type: "message"
      },
      interval: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { label: `Intervalo ${data.sec} seg.`, sec: data.sec },
        type: "interval"
      },
      condition: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: {
          key: data.key,
          condition: data.condition,
          value: data.value
        },
        type: "condition"
      },
      menu: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: {
          message: data.message,
          arrayOption: data.arrayOption,
          fallbackMessage: data.fallbackMessage || ""
        },
        type: "menu"
      },
      buttons: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: {
          message: data.message,
          arrayButtons: data.arrayButtons,
          fallbackMessage: data.fallbackMessage || ""
        },
        type: "buttons"
      },
      img: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { url: data.url },
        type: "img"
      },
      audio: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { url: data.url, record: data.record },
        type: "audio"
      },
      randomizer: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { ...data },
        type: "randomizer"
      },
      video: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { url: data.url },
        type: "video"
      },
      singleBlock: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { ...data },
        type: "singleBlock"
      },
      ticket: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { ...data },
        type: "ticket"
      },
      question: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { ...data },
        type: "question"
      },
      inactivity: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { ...data },
        type: "inactivity"
      },
      tag: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { ...data },
        type: "tag"
      },
      attendant: {
        id: geraStringAleatoria(30),
        position: { x: posX, y: posY },
        data: { ...data },
        type: "attendant"
      }
    };

    if (type === "start") {
      // Se for "start", substitui o nó inicial
      setNodes(old => [...old.filter(item => item.id !== "1"), newNodes[type]]);
    } else {
      setNodes(old => [...old, newNodes[type]]);
    }
  };

  // Função específica para adicionar um nó de "tag"
  const tagAdd = data => {
    addNode("tag", data);
  };

  // Função específica para adicionar um nó de "attendant"
  const attendantAdd = data => {
    addNode("attendant", data);
  };

  // Atualiza um nó existente
  const updateNode = (dataAlter) => {
    setNodes(old =>
      old.map(itemNode => {
        if (itemNode.id === dataAlter.id) {
          return dataAlter;
        }
        return itemNode;
      })
    );

    setModalStates({
      text: null,
      interval: false,
      condition: null,
      menu: null,
      buttons: null,
      img: null,
      audio: null,
      randomizer: null,
      video: null,
      singleBlock: null,
      ticket: null,
      modalAddTag: null,
      modalAddAttendant: null,
      question: null,
      inactivity: null
    });
  };

  const handleSaveFlow = async () => {
    try {
      setLoading(true);
      await api.post("/flowbuilder/flow", {
        idFlow: id,
        nodes: nodes,
        connections: edges
      });
      toast.success("Fluxo salvo com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFlow = async (file) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const flowData = JSON.parse(event.target.result);
        if (flowData.nodes && flowData.edges) {
          setNodes(flowData.nodes);
          setEdges(flowData.edges);

          // Salva no backend assim que importar
          try {
            setLoading(true);
            await api.post("/flowbuilder/flow", {
              idFlow: id,
              nodes: flowData.nodes,
              connections: flowData.edges
            });
            toast.success("Fluxo importado e salvo com sucesso!");
          } catch (err) {
            toastError(err);
          } finally {
            setLoading(false);
          }
        } else {
          toast.error("Arquivo inválido. O arquivo deve conter nodes e edges.");
        }
      } catch (error) {
        toast.error("Erro ao importar o fluxo. Verifique se o arquivo é válido.");
      }
    };
    reader.readAsText(file);
  };

  const onConnect = (params) => {
    setEdges(eds => addEdge({ ...params, type: 'buttonedge' }, eds));
  };

  // Abre o modal correto ao dar duplo clique no nó
  const handleDoubleClick = (event, node) => {
    setDataNode(node);
    const modalMap = {
      message: "text",
      interval: "interval",
      condition: "condition",
      menu: "menu",
      buttons: "buttons",
      img: "img",
      audio: "audio",
      randomizer: "randomizer",
      video: "video",
      singleBlock: "singleBlock",
      ticket: "ticket",
      question: "question",
      tag: "modalAddTag",
      attendant: "modalAddAttendant",
      inactivity: "inactivity"
    };

    const modalType = modalMap[node.type];
    if (modalType) {
      setModalStates(prev => ({ ...prev, [modalType]: "edit" }));
    }
  };

  // Remove uma aresta se der 3 cliques nela
  const onEdgesDelete = (edgesToDelete) => {
    setEdges((eds) => eds.filter((e) => !edgesToDelete.includes(e)));
  };

  const handleEdgeClick = (event, edge) => {
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === edge.id) {
          return { ...e, selected: true };
        }
        return { ...e, selected: false };
      })
    );
  };

  // Muda estilo do nó selecionado
  const handleNodeClick = (event, node) => {
    setNodes(old =>
      old.map(item => ({
        ...item,
        style: {
          ...item.style,
          backgroundColor:
            item.id === node.id ? theme.palette.primary.main : theme.palette.background.paper,
          padding: item.id === node.id ? 1 : 0,
          borderRadius: 8
        }
      }))
    );
  };

  // Organiza layout automaticamente
  const organizeLayout = () => {
    const HORIZONTAL_SPACING = 300;
    const VERTICAL_SPACING = 150;
    const START_X = 100;
    const START_Y = 100;

    let organizedNodes = [...nodes];

    let startNode = organizedNodes.find(node => node.id === "1");
    if (!startNode) return;

    startNode.position = { x: START_X, y: START_Y };

    let processedNodes = new Set([startNode.id]);

    const getChildNodes = (nodeId) => {
      return edges
        .filter(edge => edge.source === nodeId)
        .map(edge => ({
          edge,
          node: organizedNodes.find(n => n.id === edge.target)
        }))
        .filter(({ node }) => node !== undefined);
    };

    const getNodeTypePriority = (type) => {
      const priorities = {
        start: 0,
        ticket: 1,
        question: 2,
        message: 3,
        menu: 4,
        condition: 5,
        interval: 6,
        randomizer: 7,
        singleBlock: 8,
        img: 9,
        audio: 10,
        video: 11
      };
      return priorities[type] || 999;
    };

    const organizeNodesRecursively = (parentNode, level, position) => {
      const children = getChildNodes(parentNode.id);

      // Ordena filhos por prioridade
      children.sort((a, b) => {
        return getNodeTypePriority(a.node.type) - getNodeTypePriority(b.node.type);
      });

      children.forEach((child, index) => {
        if (processedNodes.has(child.node.id)) return;

        let x = parentNode.position.x + HORIZONTAL_SPACING;
        let y = parentNode.position.y;

        if (children.length > 1) {
          const totalHeight = (children.length - 1) * VERTICAL_SPACING;
          y += index * VERTICAL_SPACING - totalHeight / 2;
        }

        // Ajustes finos para certos tipos de nó
        switch (child.node.type) {
          case "ticket":
            y -= 50;
            break;
          case "start":
            x += 50;
            break;
          case "condition":
          case "randomizer":
            y += 25;
            break;
          default:
            break;
        }

        child.node.position = { x, y };
        processedNodes.add(child.node.id);

        organizeNodesRecursively(child.node, level + 1, index);
      });
    };

    organizeNodesRecursively(startNode, 0, 0);

    setNodes([...organizedNodes]);
    toast.success("Layout organizado com sucesso!");
  };

  // Carrega o fluxo salvo do backend
  useEffect(() => {
    const loadFlow = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/flowbuilder/flow/${id}`);
        if (data.flow.flow !== null) {
          setNodes(data.flow.flow.nodes);
          setEdges(data.flow.flow.connections);
        }
        
        // Armazenar o nome do fluxo
        if (data.flow) {
          setFlowName(data.flow.name);
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    loadFlow();
  }, [id]);

  // Observa ações de deletar ou duplicar nós vindas do store
  useEffect(() => {
    if (storageItems.action === "delete") {
      setNodes(old => old.filter(item => item.id !== storageItems.node));
      setEdges(old => {
        const newData = old.filter(item => item.source !== storageItems.node);
        const newClearTarget = newData.filter(item => item.target !== storageItems.node);
        return newClearTarget;
      });
      storageItems.setNodesStorage("");
      storageItems.setAct("idle");
    }
    if (storageItems.action === "duplicate") {
      const nodeDuplicate = nodes.find(item => item.id === storageItems.node);
      if (!nodeDuplicate) return;
      const newNode = JSON.parse(JSON.stringify(nodeDuplicate));
      newNode.id = geraStringAleatoria(30);
      newNode.position = {
        x: nodeDuplicate.position.x + 240,
        y: nodeDuplicate.position.y
      };
      newNode.selected = false;
      setNodes(old => [...old, newNode]);
      storageItems.setNodesStorage("");
      storageItems.setAct("idle");
    }
  }, [storageItems.action, storageItems.node, nodes]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const handleActionClick = (type) => {
    const actionTypeToModal = {
      start: () => addNode("start"),
      menu: () => setModalStates(prev => ({ ...prev, menu: "create" })),
      buttons: () => setModalStates(prev => ({ ...prev, buttons: "create" })),
      content: () => setModalStates(prev => ({ ...prev, singleBlock: "create" })),
      random: () => setModalStates(prev => ({ ...prev, randomizer: "create" })),
      interval: () => setModalStates(prev => ({ ...prev, interval: "create" })),
      ticket: () => setModalStates(prev => ({ ...prev, ticket: "create" })),
      question: () => setModalStates(prev => ({ ...prev, question: "create" })),
      tag: () => setModalStates(prev => ({ ...prev, modalAddTag: "create" })),
      attendant: () => setModalStates(prev => ({ ...prev, modalAddAttendant: "create" })),
      fallback: () => setModalStates(prev => ({ ...prev, fallback: "create" })),
      inactivity: () => setModalStates(prev => ({ ...prev, inactivity: "create" }))
    };

    const handler = actionTypeToModal[type];
    if (handler) handler();
  };

  return (
    <div className={classes.mainContainer}>
      <Paper
        elevation={0}
        className={classes.header}
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary
        }}
      >
        <Stack direction="row" alignItems="center">
          <Button
            className={classes.backButton}
            startIcon={<ArrowBack />}
            onClick={() => history.goBack()}
            sx={{
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            Voltar
          </Button>
          <Stack direction="column">
            <Title>Fluxo: {flowName}</Title>
            <Typography variant="body2" color="text.secondary">
              Desenhe seu fluxo de conversa
            </Typography>
          </Stack>
        </Stack>
        <Box
          className={classes.actionButtons}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider
          }}
        >
          <Stack direction="row" spacing={2}>
            <input
              type="file"
              id="flow-import"
              accept=".json"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleImportFlow(file);
                }
                e.target.value = null;
              }}
            />
            <Button
              variant="contained"
              onClick={() => {
                const flowData = {
                  nodes,
                  edges
                };
                const blob = new Blob([JSON.stringify(flowData, null, 2)], {
                  type: "application/json"
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `flow-${id}-${format(new Date(), "yyyy-MM-dd-HH-mm")}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                toast.success("Fluxo exportado com sucesso!");
              }}
              startIcon={<GetApp />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark
                }
              }}
            >
              Exportar Fluxo
            </Button>
            <Button
              variant="contained"
              onClick={() => document.getElementById("flow-import").click()}
              startIcon={<Upload />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark
                }
              }}
            >
              Importar Fluxo
            </Button>
            <Button
              variant="contained"
              onClick={organizeLayout}
              startIcon={<AutoFixIcon />}
              sx={{
                backgroundColor: theme.palette.success.main,
                color: theme.palette.common.white,
                "&:hover": {
                  backgroundColor: theme.palette.success.dark
                }
              }}
            >
              Organizar Layout
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveFlow}
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark
                }
              }}
            >
              Salvar Fluxo
            </Button>
          </Stack>
        </Box>
      </Paper>

      <div className={classes.flowContainer}>
        <SpeedDial
          ariaLabel="Adicionar elementos"
          className={classes.speedDial}
          icon={<SpeedDialIcon />}
          direction="down"
        >
          {console.log("Renderizando SpeedDial")}
          {actions.map(action => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              tooltipOpen
              tooltipPlacement="right"
              onClick={() => handleActionClick(action.type)}
            />
          ))}
        </SpeedDial>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDoubleClick={handleDoubleClick}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          onEdgesDelete={onEdgesDelete}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          connectionLineStyle={connectionLineStyle}
          defaultEdgeOptions={{
            type: "buttonedge",
            style: { strokeWidth: "5px" },
            animated: true
          }}
          fitView
        >
          <Controls />
          
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>

        {/* Modais */}
        <FlowBuilderAddTextModal
          open={modalStates.text}
          onSave={data => addNode("text", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, text: null }))}
        />
        <FlowBuilderIntervalModal
          open={modalStates.interval}
          onSave={data => addNode("interval", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, interval: false }))}
        />
        <FlowBuilderConditionModal
          open={modalStates.condition}
          onSave={data => addNode("condition", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, condition: null }))}
        />
        <FlowBuilderMenuModal
          open={modalStates.menu}
          onSave={data => addNode("menu", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, menu: null }))}
        />
        <FlowBuilderButtonsModal
          open={modalStates.buttons}
          onSave={data => addNode("buttons", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, buttons: null }))}
        />
        <FlowBuilderAddImgModal
          open={modalStates.img}
          onSave={data => addNode("img", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, img: null }))}
        />
        <FlowBuilderAddAudioModal
          open={modalStates.audio}
          onSave={data => addNode("audio", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, audio: null }))}
        />
        <FlowBuilderRandomizerModal
          open={modalStates.randomizer}
          onSave={data => addNode("randomizer", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, randomizer: null }))}
        />
        <FlowBuilderAddVideoModal
          open={modalStates.video}
          onSave={data => addNode("video", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, video: null }))}
        />
        <FlowBuilderSingleBlockModal
          open={modalStates.singleBlock}
          onSave={data => addNode("singleBlock", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, singleBlock: null }))}
        />
        <FlowBuilderTicketModal
          open={modalStates.ticket}
          onSave={data => addNode("ticket", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, ticket: null }))}
        />
        <FlowBuilderAddQuestionModal
          open={modalStates.question}
          onSave={data => addNode("question", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, question: null }))}
        />
        <FlowBuilderAddTagModal
          open={modalStates.modalAddTag}
          onSave={data => tagAdd(data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, modalAddTag: null }))}
        />
        {/* Modal de atendente */}
        <FlowBuilderAddAttendantModal
          open={modalStates.modalAddAttendant}
          onSave={data => attendantAdd(data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, modalAddAttendant: null }))}
        />
        {/* Modal de retorno (fallback) */}
        <FlowBuilderFallbackModal
          open={modalStates.fallback}
          onSave={data => addNode("fallback", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, fallback: null }))}
        />
        <FlowBuilderInactivityModal
          open={modalStates.inactivity}
          onSave={data => addNode("inactivity", data)}
          data={dataNode}
          onUpdate={updateNode}
          close={() => setModalStates(prev => ({ ...prev, inactivity: null }))}
        />
      </div>
    </div>
  );
};

export default FlowBuilderConfig;
